import type {
  BaseTokenInfo,
  SupportedChainName,
  TokenDeployment,
  TokenInfo,
} from "@src/components/DefuseSDK/types/base"
import { isBaseToken } from "@src/components/DefuseSDK/utils/token"
import {
  resolveTokenFamily,
  type TokenFamilyList,
} from "@src/components/DefuseSDK/utils/tokenFamily"
import {
  eachBaseTokenInfo,
  getAnyBaseTokenInfo,
  getUnderlyingBaseTokenInfos,
} from "@src/components/DefuseSDK/utils/tokenUtils"
import {
  getHyperliquidSrcChain,
  isDirectHyperliquidToken,
  isHyperliquid,
} from "./hyperliquid"

export function resolveTokenOut(
  blockchain: SupportedChainName | "near_intents",
  tokenIn: TokenInfo,
  tokenFamilies: TokenFamilyList,
  tokenList: TokenInfo[]
): [BaseTokenInfo, TokenDeployment] {
  if (blockchain === "near_intents") {
    const token = getAnyBaseTokenInfo(tokenIn)
    return [token, token.deployments[0]]
  }

  if (isHyperliquid(blockchain)) {
    const directTokenOut = getDirectHyperliquidTokenOut(
      tokenIn,
      tokenFamilies,
      tokenList
    )
    if (directTokenOut != null) {
      return directTokenOut
    }

    blockchain = (
      {
        bitcoin: "bitcoin",
        solana: "solana",
        ethereum: "eth",
      } as const
    )[getHyperliquidSrcChain(tokenIn)]
  }

  const tf = resolveTokenFamily(tokenFamilies, tokenIn)

  const relatedTokenIds =
    tf?.tokenIds ??
    getUnderlyingBaseTokenInfos(tokenIn).map((t) => t.defuseAssetId)

  for (const token of eachBaseTokenInfo(tokenList)) {
    if (!relatedTokenIds.includes(token.defuseAssetId)) {
      continue
    }

    const depl = token.deployments.find((depl) => depl.chainName === blockchain)
    if (depl != null) {
      return [token, depl]
    }
  }

  throw new Error("No corresponded token found")
}

function getDirectHyperliquidTokenOut(
  tokenIn: TokenInfo,
  tokenFamilies: TokenFamilyList,
  tokenList: TokenInfo[]
): [BaseTokenInfo, TokenDeployment] | null {
  let tokenOut = isBaseToken(tokenIn)
    ? isDirectHyperliquidToken(tokenIn)
      ? tokenIn
      : null
    : tokenIn.groupedTokens.find(isDirectHyperliquidToken)

  if (tokenOut == null) {
    const tf = resolveTokenFamily(tokenFamilies, tokenIn)
    if (tf != null) {
      for (const token of eachBaseTokenInfo(tokenList)) {
        if (
          tf.tokenIds.includes(token.defuseAssetId) &&
          isDirectHyperliquidToken(token)
        ) {
          tokenOut = token
          break
        }
      }
    }
  }

  if (tokenOut == null) return null

  const deployment = tokenOut.deployments.find(
    (depl) => depl.chainName === "hyperliquid"
  )
  return deployment != null ? [tokenOut, deployment] : null
}
